import React, { useState, useEffect, useCallback } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import { connect } from 'react-redux';
import { iRootDispatch, iRootState } from '../../store';
import { FaFire, FaSignOutAlt } from 'react-icons/fa';
import logorv from '../../resources/imgs/logo-lado-bom-seguros.svg';
import { InsurersCodeEnum } from '../../enums/insurers-code.enum';
import { isEstateRole } from '../../utils/isEstateRole';
import { FieldLabel } from '../../styles';
import {
  Container,
  Header,
  HeaderLogo,
  LogoutContext,
  Content,
  SubHeader,
  AnalysisTitle,
  CardHeader,
  IconWrapper,
  CardTitle,
  Description,
  FormSection,
  FooterButtons,
  BackButton
} from './styles';
import { Dropdown, Dimmer, Loader as SemanticLoader } from 'semantic-ui-react';
import { cleanUserSession } from '../../services/auth';
import { getFireInsuranceActiveInsurers } from '../../services/insurer';
import { getBroker } from '../../services/broker';
import { FireInsuranceActiveInsurersResponseDTO } from '../../dtos/fire-insurance-budget/fire-insurance-active-insurers-response.dto';
import PwaAlfaBudgetForm from './forms/PwaAlfaBudgetForm';
import PwaSegimobBudgetForm from './forms/PwaSegimobBudgetForm';
import PwaYelumBudgetForm from './forms/PwaYelumBudgetForm';

interface Props extends RouteComponentProps {
  user: any;
  availableBrokersAndEstates: any;
}

const PwaFireInsuranceBudget: React.FC<Props> = ({ user, history, availableBrokersAndEstates }) => {
  const [estates, setEstates] = useState<any[]>([]);
  const [selectedEstate, setSelectedEstate] = useState<any>(null);
  const [insurers, setInsurers] = useState<any[]>([]);
  const [selectedInsurer, setSelectedInsurer] = useState<any>(null);
  const [loadingInsurers, setLoadingInsurers] = useState(false);
  const [commissionPercentage, setCommissionPercentage] = useState<number>(0);

  const isEstateUser = isEstateRole(user?.role);

  const [loadingInitial, setLoadingInitial] = useState(true);

  useEffect(() => {
    const loadEstates = async () => {
      try {
        if (availableBrokersAndEstates?.estates && availableBrokersAndEstates.estates.length > 0) {
          const estateOptions = availableBrokersAndEstates.estates.map((e: any) => ({
            key: e.value?.id || e.text,
            value: e.value?.id,
            text: e.text,
            originalValue: e.value
          }));
          setEstates(estateOptions);
          if (estateOptions.length === 1) {
            setSelectedEstate(estateOptions[0]);
          }
          setLoadingInitial(false);
          return;
        }

        // Se o Redux estiver vazio (ex: reload no PWA)
        if (user.estate?.id) {
          const singleEstate = [{
            key: user.estate.id,
            value: user.estate.id,
            text: user.estate.name || 'Minha Imobiliária',
            originalValue: user.estate
          }];
          setEstates(singleEstate);
          setSelectedEstate(singleEstate[0]);
        } else if (user.broker?.id || user.broker) {
          // Fallback broker fetch
          const brokerIdToFetch = user.broker?.id || (typeof user.broker === 'string' ? user.broker : '7c98d9d9-a975-45a3-9131-cc1961250182');
          const brokerDTO = await getBroker(brokerIdToFetch);
          if (brokerDTO && brokerDTO.estates) {
            const mapped = brokerDTO.estates.map((e: any) => ({
              key: e.id,
              value: e.id,
              text: e.name,
              originalValue: e
            }));
            const ordered = mapped.sort((a, b) => a.text.localeCompare(b.text));
            setEstates(ordered);
            if (ordered.length === 1) setSelectedEstate(ordered[0]);
          }
        }
      } catch (e) {
        console.error('Failed to load estates', e);
      } finally {
        setLoadingInitial(false);
      }
    };

    loadEstates();
  }, [availableBrokersAndEstates, user]);

  const fetchInsurers = useCallback(async (estateId: string) => {
    setLoadingInsurers(true);
    try {
      const response: FireInsuranceActiveInsurersResponseDTO = await getFireInsuranceActiveInsurers(estateId);

      // Filter for only Alfa, Segimob, Yelum for PWA as requested
      const targetInsurers = response.fire_insurance_active_insurers.filter(insurer =>
        [InsurersCodeEnum.alfa, InsurersCodeEnum.TokioMarineSegimob, InsurersCodeEnum.Yelum].includes(insurer.code as any)
      );

      const insurerOptions = targetInsurers.map(insurer => ({
        key: insurer.id,
        value: insurer.id,
        text: insurer.name,
        code: insurer.code,
        originalValue: insurer
      }));

      setInsurers(insurerOptions);
      if (insurerOptions.length === 1) {
        handleInsurerChange(insurerOptions[0], estateId);
      } else {
        setSelectedInsurer(null);
      }
    } catch (e) {
      console.error('Error fetching insurers', e);
    } finally {
      setLoadingInsurers(false);
    }
  }, []);

  useEffect(() => {
    const estateId = selectedEstate?.value || selectedEstate?.originalValue?.id;
    if (estateId) {
      fetchInsurers(estateId);
    } else {
      setInsurers([]);
      setSelectedInsurer(null);
    }
  }, [selectedEstate, fetchInsurers]);

  const handleInsurerChange = (insurerOption: any, estateIdOverride?: string) => {
    if (!insurerOption) {
      setSelectedInsurer(null);
      setCommissionPercentage(0);
      return;
    }

    const estateValue = selectedEstate?.originalValue
      || (typeof selectedEstate?.value === 'object' ? selectedEstate.value : null)
      || (estateIdOverride ? availableBrokersAndEstates?.estates?.find((e: any) => e.value?.id === estateIdOverride)?.value : null);
    const insurerId = insurerOption?.originalValue?.id || insurerOption?.value;
    if (estateValue && estateValue.insurer_data && insurerId) {
      const insurerData = estateValue.insurer_data.find((d: any) => d.insurer_id === insurerId);
      if (insurerData) {
        setCommissionPercentage(Number(insurerData.commission_percentage) || 0);
      }
    }
    setSelectedInsurer(insurerOption);
  };

  const handleLogout = () => {
    cleanUserSession();
    history.push('/login');
  };

  const cancelAndBack = () => {
    history.push('/pwa/home');
  };

  const renderSelectedForm = () => {
    if (!selectedInsurer || !selectedEstate) return null;

    const selectedEstateData = selectedEstate?.originalValue
      || (typeof selectedEstate?.value === 'object' ? selectedEstate.value : null)
      || availableBrokersAndEstates?.estates?.find((e: any) => e.value?.id === selectedEstate?.value)?.value;

    const selectedInsurerData = selectedInsurer?.originalValue
      || (typeof selectedInsurer?.value === 'object' ? selectedInsurer.value : null)
      || insurers.find((i: any) => i.value === selectedInsurer?.value)?.originalValue;

    const insurerId = selectedInsurerData?.id || selectedInsurer?.value;
    const insurerCode = selectedInsurerData?.code || selectedInsurer?.code;

    if (!selectedEstateData || !insurerId || !insurerCode) {
      return null;
    }

    const props = {
      estate: selectedEstateData,
      insurerId,
      userId: user.id,
      percentage: commissionPercentage,
      onBudgetCreated: (budgetId: string) => {
        // We will likely show the success screen here or within the form.
      }
    };

    switch (insurerCode) {
      case InsurersCodeEnum.alfa:
        return <PwaAlfaBudgetForm {...props} onBack={() => setSelectedInsurer(null)} />;
      case InsurersCodeEnum.TokioMarineSegimob:
        return <PwaSegimobBudgetForm {...props} onBack={() => setSelectedInsurer(null)} />;
      case InsurersCodeEnum.Yelum:
        return <PwaYelumBudgetForm {...props} onBack={() => setSelectedInsurer(null)} />;
      default:
        return null;
    }
  };

  if (loadingInitial) {
    return (
      <Container>
        <Header>
          <HeaderLogo src={logorv} alt="Logo" />
        </Header>
        <Dimmer active inverted>
          <SemanticLoader content="Carregando..." />
        </Dimmer>
      </Container>
    );
  }

  if (selectedInsurer) {
    const selectedForm = renderSelectedForm();
    if (selectedForm) return selectedForm;

    return (
      <Container>
        <Header>
          <HeaderLogo src={logorv} alt="Logo" />
        </Header>
        <Content>
          <Description>
            Nao foi possivel iniciar o formulario da seguradora selecionada. Tente novamente.
          </Description>
          <FooterButtons style={{ marginTop: 'auto' }}>
            <BackButton onClick={() => setSelectedInsurer(null)}>Voltar</BackButton>
          </FooterButtons>
        </Content>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <HeaderLogo src={logorv} alt="Logo" />
        <LogoutContext onClick={handleLogout}>
          <FaSignOutAlt />
        </LogoutContext>
      </Header>

      <Content>
        <SubHeader>
          <CardHeader>
            <IconWrapper>
              <FaFire />
            </IconWrapper>
            <CardTitle>Novo Orçamento</CardTitle>
          </CardHeader>
          <Description>
            Selecione a imobiliária e a seguradora para iniciar uma nova cotação de seguro incêndio.
          </Description>
        </SubHeader>

        <FormSection>
          {!isEstateUser && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <FieldLabel>Imobiliária</FieldLabel>
              <Dropdown
                selection
                search
                fluid
                options={estates}
                value={selectedEstate?.value || (selectedEstate?.originalValue && selectedEstate.originalValue.id)}
                onChange={(_, { value }) => setSelectedEstate(estates.find(e => e.value === value))}
                placeholder="Selecione a imobiliária..."
                disabled={estates.length === 1}
                clearable
              />
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <FieldLabel>Seguradora</FieldLabel>
            <Dropdown
              selection
              fluid
              options={insurers}
              value={selectedInsurer?.value || selectedInsurer?.originalValue?.id}
              onChange={(_, { value }) => handleInsurerChange(insurers.find((i: any) => i.value === value))}
              placeholder={loadingInsurers ? 'Carregando opções...' : 'Selecione a seguradora...'}
              disabled={!selectedEstate || loadingInsurers || insurers.length === 0}
              clearable
            />
          </div>
        </FormSection>

        <FooterButtons style={{ marginTop: 'auto' }}>
          <BackButton onClick={cancelAndBack}>Voltar</BackButton>
        </FooterButtons>
      </Content>
    </Container>
  );
};

const mapStateToProps = (state: iRootState) => ({
  user: state.user,
  availableBrokersAndEstates: state.availableBrokersAndEstates
});

export default connect(mapStateToProps)(PwaFireInsuranceBudget);
